import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import { AskMarkdown } from './AskMarkdown';
import type { AskMarkdownSource, AskMarkdownSourceOpener } from './AskMarkdown';
import { DocumentReader } from '../../word/api/DocumentReader';
import { DocumentExporter } from '../../word/utils/DocumentExporter';
import { ChatComposer } from './ChatComposer';
import { RunActivity } from './conversation/RunActivity';
import { ConversationHeaderActions, SavedConversations } from './conversation/SavedConversations';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import {
  getReviewPartyContextInstruction,
  hasReviewPartyContextComplete,
} from '../../utils/reviewPartyContext';
import apiService from '../../services/api';
import { activitySummary, turnsFromServer } from '../../features/ask/askConversation';
import { findCitedParagraph } from '../../features/ask/citedParagraph';
import type { AskActivity, AskConversationMeta, AskTurn } from '../../features/ask/askConversation';
import { BankOfChangesServerError } from '../../features/agent/skills/bankOfChanges/types';
import { fileToBase64Payload, filesToAttachmentPayloads } from '../../utils/fileAttachments';
import { handleError } from '../../utils/errorHandling';
import { logError } from '../../utils/logging';
import './conversation/conversationLook.css';
import './SkillRunner.css';
import './BankOfChangesRunner.css';
import './AskView.css';

/** The most of the open document's text that travels with a question (backend `DocumentAskRequest`). */
const DOCUMENT_ASK_MAX_CHARS = 200_000;
/** The largest Word file the server keeps with a conversation (backend `ASK_DOCUMENT_MAX_FILE_MB`). */
const ASK_DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;
/** How often the pane asks the server how far an answer got. */
const FOLLOW_INTERVAL_MS = 1200;
/** How often the list of conversations is read again while one of them is being answered. */
const LIST_REFRESH_MS = 4000;
/** The name the server gives the open document when the pane did not say its name (backend `_start_ask`). */
const OPEN_DOCUMENT_UNNAMED = 'המסמך הפתוח ב-Word';
/** How long the notice that a source is no longer in the document stays. */
const SOURCE_NOTICE_MS = 5000;

/**
 * The conversation that was open when "Ask" was last left, by its id on the server. Held in memory
 * only, for as long as the pane lives: coming back to "Ask" opens it again from the server, with the
 * answer that was being worked on meanwhile. Nothing of a conversation is kept on this computer.
 */
let lastOpenConversation: string | null = null;

/** Another user is signing in to this pane: "Ask" opens on a new conversation, not on the last one's. */
export function forgetLastOpenAskConversation(): void {
  lastOpenConversation = null;
}

/** The open document's file name; empty when Word does not say. */
async function openDocumentName(): Promise<string> {
  try {
    return await DocumentExporter.getDocumentName();
  } catch (e) {
    logError('[AskView] reading the document name failed', e);
    return '';
  }
}

/**
 * Hands the server a copy of the open document for a conversation that was just
 * opened on it. The dashboard's chat shows the file at the conversation's head.
 * A document that cannot be read, or is too large, leaves the conversation with
 * the file's name alone.
 */
async function keepDocumentWithConversation(conversationId: string, documentName: string): Promise<void> {
  try {
    const file = await DocumentExporter.getDocumentAsFile(documentName || 'document.docx');
    if (file.size > ASK_DOCUMENT_MAX_BYTES) return;
    const payload = await fileToBase64Payload(file);
    await apiService.attachAskConversationDocument(conversationId, {
      filename: file.name,
      content_base64: payload.content_base64,
    });
  } catch (e) {
    logError('[AskView] keeping the document with the conversation failed', e);
  }
}

function isWordApiAvailable(): boolean {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
}

export interface AskViewProps {
  /** When party context is missing, parent switches to Review in the same task pane. */
  onRequireReview?: () => void;
}

/**
 * "Ask": a chat about the document open in Word and the files attached to it.
 *
 * An answer is worked out by an agent in a run that goes on in the server: the
 * pane starts it, shows its steps as they come, and reads the answer from the
 * conversation when the run is over. Leaving "Ask", or the pane closing, does
 * not end the run; the stop button does. The conversations are the server's:
 * the list behind the history button opens any of them, and the dashboard's
 * chat shows the same ones.
 */
export const AskView: React.FC<AskViewProps> = ({ onRequireReview }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [documentText, setDocumentText] = React.useState('');
  const [docLoading, setDocLoading] = React.useState(true);
  const [docError, setDocError] = React.useState<string | null>(null);
  const [turns, setTurns] = React.useState<AskTurn[]>([]);
  const [draft, setDraft] = React.useState('');
  /** The open conversation's id on the server; null until its first question was asked. */
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  /** The Word file the open conversation was held on, as the server names it. */
  const [heldOn, setHeldOn] = React.useState('');
  /** The name of the document open in Word now. */
  const [openName, setOpenName] = React.useState('');
  /** A question is on its way to the server. */
  const [starting, setStarting] = React.useState(false);
  /** An answer is being worked on in the server and is followed from here. */
  const [following, setFollowing] = React.useState(false);
  const [live, setLive] = React.useState<AskActivity | null>(null);
  const [stopping, setStopping] = React.useState(false);
  const [sessions, setSessions] = React.useState<AskConversationMeta[] | null>(null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [failedId, setFailedId] = React.useState<string | null>(null);
  /** The answer whose source could not be found in the open document, said under it for a moment. */
  const [sourceMissing, setSourceMissing] = React.useState<string | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const mounted = React.useRef(true);
  /** Tells one opened conversation from the next: an answer that arrives for a conversation that was left is dropped. */
  const opened = React.useRef(0);
  const busy = starting || following;

  const activityCopy = React.useMemo(
    () => ({
      starting: t('ask.thinking'),
      done: (steps: number) => (steps === 1 ? t('ask.activityDoneOne') : t('ask.activityDone', { n: steps })),
      failed: t('ask.activityFailed'),
      stopped: t('ask.activityStopped'),
      stage: (stage: string) => t(`ask.stage.${stage}`, { defaultValue: stage }),
    }),
    [t],
  );

  const refreshList = React.useCallback(() => {
    apiService
      .listAskConversations()
      .then((list) => {
        if (mounted.current) setSessions(list);
      })
      .catch((e) => logError('[AskView] listing saved conversations failed', e));
  }, []);

  const loadDocument = React.useCallback(async () => {
    if (!isWordApiAvailable()) {
      setDocError(t('ask.wordApiUnavailable'));
      setDocumentText('');
      setDocLoading(false);
      return;
    }
    setDocLoading(true);
    setDocError(null);
    try {
      const text = await DocumentReader.getFullDocumentText();
      setDocumentText(text || '');
      if (!text?.trim()) {
        setDocError(t('ask.emptyDocument'));
      }
    } catch (e) {
      setDocError(handleError(e));
      setDocumentText('');
    } finally {
      setDocLoading(false);
    }
  }, [t]);

  /** Reads a conversation from the server and shows it; a run that is going on in it is followed. */
  const showConversation = React.useCallback(async (id: string): Promise<boolean> => {
    const mine = (opened.current += 1);
    try {
      const found = await apiService.getAskConversation(id);
      if (!mounted.current || mine !== opened.current) return true;
      setTurns(turnsFromServer(found.messages));
      setConversationId(id);
      lastOpenConversation = id;
      setHeldOn(found.documentName);
      setStarting(false);
      setStopping(Boolean(found.run?.stop_requested));
      setLive(found.run?.activity ?? null);
      setFollowing(found.run !== null);
      return true;
    } catch (e) {
      logError('[AskView] opening a saved conversation failed', e);
      return false;
    }
  }, []);

  React.useEffect(() => {
    // Set here and not only at first render: in development React runs every effect twice.
    mounted.current = true;
    void loadDocument();
    void openDocumentName().then((name) => {
      if (mounted.current) setOpenName(name);
    });
    refreshList();
    // Coming back to "Ask": the conversation that was open is read again from the server.
    if (lastOpenConversation) {
      const id = lastOpenConversation;
      void showConversation(id).then((ok) => {
        if (!ok && lastOpenConversation === id) lastOpenConversation = null;
      });
    }
    return () => {
      // Leaving only stops the asking: the answer goes on in the server.
      mounted.current = false;
    };
  }, [loadDocument, refreshList, showConversation]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, busy, live]);

  // An answer goes on in the server: the pane asks how far it got, and when it is over reads the conversation again.
  React.useEffect(() => {
    if (!following || !conversationId) return undefined;
    const id = conversationId;
    const mine = opened.current;
    let timer: number | null = null;
    let active = true;
    const ask = async () => {
      try {
        const status = await apiService.getChatRun(id);
        if (!active || mine !== opened.current) return;
        if (status.running) {
          setLive(status.run?.activity ?? null);
          setStopping(Boolean(status.run?.stop_requested));
        } else {
          const found = await apiService.getAskConversation(id);
          if (!active || mine !== opened.current) return;
          setTurns(turnsFromServer(found.messages));
          setLive(null);
          setStopping(false);
          setFollowing(false);
          refreshList();
          return;
        }
      } catch (e) {
        // The connection may be down for a moment; the run goes on in the server either way.
        logError('[AskView] asking how far the answer got failed', e);
      }
      if (active) timer = window.setTimeout(() => void ask(), FOLLOW_INTERVAL_MS);
    };
    timer = window.setTimeout(() => void ask(), 600);
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [following, conversationId, refreshList]);

  const runningCount = (sessions ?? []).filter((item) => item.running).length;
  const savedCount = sessions?.length ?? 0;

  // While a conversation is being answered, and while the list is open, the list is read again every few
  // seconds: a row says that an answer is being worked on for as long as it is true and no longer.
  React.useEffect(() => {
    if (runningCount === 0 && !panelOpen) return undefined;
    const timer = window.setInterval(refreshList, LIST_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [runningCount, panelOpen, refreshList]);

  const startNew = () => {
    opened.current += 1;
    lastOpenConversation = null;
    setConversationId(null);
    setHeldOn('');
    setTurns([]);
    setStarting(false);
    setFollowing(false);
    setLive(null);
    setStopping(false);
    setFailedId(null);
    setPanelOpen(false);
  };

  const handleOpen = (id: string) => {
    if (id === conversationId) {
      setPanelOpen(false);
      return;
    }
    setFailedId(null);
    void showConversation(id).then((ok) => {
      if (!mounted.current) return;
      if (ok) setPanelOpen(false);
      else setFailedId(id);
    });
  };

  const handleDelete = (id: string) => {
    apiService
      .deleteAskConversation(id)
      .then(() => {
        if (!mounted.current) return;
        // Deleting the open conversation leaves a new, empty one in its place.
        if (id === conversationId) startNew();
        refreshList();
      })
      .catch((e) => logError('[AskView] deleting a saved conversation failed', e));
  };

  const handleStop = () => {
    if (!conversationId || stopping) return;
    setStopping(true);
    apiService.stopChatRun(conversationId).catch((e) => {
      logError('[AskView] asking the answer to stop failed', e);
      if (mounted.current) setStopping(false);
    });
  };

  const handleSend = async (text: string, attachmentFiles: File[]): Promise<boolean> => {
    const trimmed = text.trim();
    if ((!trimmed && attachmentFiles.length === 0) || busy) return false;

    if (!documentText.trim()) {
      return false;
    }

    if (!hasReviewPartyContextComplete()) {
      onRequireReview?.();
      return false;
    }

    const userDisplay =
      trimmed ||
      (attachmentFiles.length
        ? t('ask.attached', { names: attachmentFiles.map((f) => f.name).join(', ') })
        : '');
    const mine = opened.current;
    setTurns((prev) => [
      ...prev,
      {
        id: `u-${Date.now()}`,
        role: 'user',
        content: userDisplay,
        // A turn of files alone already names them in its text.
        attachments: trimmed && attachmentFiles.length > 0 ? attachmentFiles.map((f) => f.name) : undefined,
      },
    ]);
    setStarting(true);
    setLive(null);
    setStopping(false);

    try {
      const attachments =
        attachmentFiles.length > 0 ? await filesToAttachmentPayloads(attachmentFiles) : undefined;
      const documentName = openName || (await openDocumentName());
      const started = await apiService.startAsk({
        documentText: documentText.length > DOCUMENT_ASK_MAX_CHARS ? documentText.slice(0, DOCUMENT_ASK_MAX_CHARS) : documentText,
        question:
          trimmed ||
          (attachmentFiles.length
            ? 'Use the attached reference document(s) to answer in context of the open document.'
            : ''),
        reviewPartyContext: getReviewPartyContextInstruction(),
        attachments,
        conversationId,
        documentName,
        // An attachment-only message is asked in fixed words; the conversation keeps what the lawyer saw.
        shownQuestion: trimmed ? undefined : userDisplay,
      });
      if (started.conversationId !== conversationId) {
        // A conversation the server just opened gets its document, in the background.
        void keepDocumentWithConversation(started.conversationId, documentName);
        lastOpenConversation = started.conversationId;
      }
      if (!mounted.current || mine !== opened.current) return true;
      if (started.conversationId !== conversationId) {
        setConversationId(started.conversationId);
        setHeldOn(documentName);
      }
      setLive(started.status.run?.activity ?? null);
      setFollowing(true);
      setStarting(false);
      refreshList();
      return true;
    } catch (e) {
      if (!mounted.current || mine !== opened.current) return true;
      // A reason the server wrote for the lawyer is shown as it is.
      const detail = e instanceof BankOfChangesServerError ? e.message : handleError(e);
      setTurns((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: 'assistant', content: t('ask.sendFailed', { detail }), notice: 'failed' },
      ]);
      setStarting(false);
      return true;
    }
  };

  React.useEffect(() => {
    if (!sourceMissing) return undefined;
    const timer = window.setTimeout(() => setSourceMissing(null), SOURCE_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [sourceMissing]);

  /**
   * A source of the open document opens at its paragraph. The document is read as it is now, and the
   * paragraph is found by the words the source quotes: the document may have changed since the answer.
   */
  const sourceOpener = (turnId: string): AskMarkdownSourceOpener => ({
    canOpen: (source: AskMarkdownSource) =>
      isWordApiAvailable() && (source.filename === OPEN_DOCUMENT_UNNAMED || (!!openName && source.filename === openName)),
    open: (source: AskMarkdownSource) => {
      setSourceMissing(null);
      void (async () => {
        try {
          const { paragraphs } = await DocumentReader.getFullDocumentContext();
          const index = findCitedParagraph(paragraphs, source);
          if (index !== null && (await DocumentReader.navigateToParagraph(index))) return;
        } catch (e) {
          logError('[AskView] opening a source in the document failed', e);
        }
        if (mounted.current) setSourceMissing(turnId);
      })();
    },
    label: t('ask.openSource'),
  });

  const liveActivity: AskActivity | null = busy ? (live ?? { kind: 'ask', state: 'running', clusters: [] }) : null;
  const inputDisabled = docLoading || !documentText.trim();
  const otherDocument = Boolean(conversationId && heldOn && openName && heldOn !== openName);
  const now = Date.now();

  /** When a conversation was last touched, in a few words. */
  const whenText = (updatedAt: number): string => {
    if (!Number.isFinite(updatedAt)) return '';
    const minutes = Math.max(0, Math.round((now - updatedAt) / 60000));
    if (minutes < 1) return t('ask.saved.justNow');
    if (minutes < 60) return minutes === 1 ? t('ask.saved.minutesAgoOne') : t('ask.saved.minutesAgo', { n: minutes });
    const hours = Math.round(minutes / 60);
    if (hours < 24) return hours === 1 ? t('ask.saved.hoursAgoOne') : t('ask.saved.hoursAgo', { n: hours });
    return new Date(updatedAt).toLocaleDateString();
  };

  const activityBlock = (activity: AskActivity) => (
    <RunActivity
      state={activity.state}
      clusters={activity.clusters}
      summary={stopping && activity.state === 'running' ? t('ask.stopping') : activitySummary(activity, activityCopy)}
      toggleTitle={t('ask.activityToggle')}
      titleOf={(cluster) => activityCopy.stage(cluster.stage)}
      rtl={chromeDir === 'rtl'}
    />
  );

  return (
    <div className="skill-runner boc-chat ask-view" dir={chromeDir}>
      <div className="skill-runner-header ask-header">
        <span className="skill-runner-title ask-title">{t('ask.title')}</span>
        <ConversationHeaderActions
          savedLabel={t('ask.saved.title')}
          savedTitle={
            runningCount > 0
              ? runningCount === 1
                ? t('ask.saved.runningCountOne')
                : t('ask.saved.runningCount', { n: runningCount })
              : savedCount > 0
                ? savedCount === 1
                  ? t('ask.saved.countOne')
                  : t('ask.saved.count', { n: savedCount })
                : t('ask.saved.title')
          }
          newLabel={t('ask.saved.new')}
          savedCount={savedCount}
          runningCount={runningCount}
          panelOpen={panelOpen}
          onTogglePanel={() => {
            if (!panelOpen) refreshList();
            setFailedId(null);
            setPanelOpen((value) => !value);
          }}
          onNew={startNew}
        />
      </div>

      <div className="boc-body">
        <div className="skill-runner-thread ask-messages">
          {turns.length === 0 && !busy && (
            <div className="ask-empty" dir={chromeDir}>
              {docLoading ? (
                <p className="ask-empty-loading">{t('ask.readingDocument')}</p>
              ) : docError ? (
                <p className="ask-empty-error">{docError}</p>
              ) : (
                <>
                  <MessageCircle className="ask-empty-icon" size={40} />
                  <p>{t('ask.emptyTitle')}</p>
                  <p className="ask-empty-hint">{t('ask.emptyHint')}</p>
                </>
              )}
            </div>
          )}

          {otherDocument && (
            <div className="ask-held-on" dir={chromeDir}>
              {t('ask.heldOnOther', { name: heldOn })}
            </div>
          )}

          {turns.map((turn) => {
            const dir = detectTextDirection(turn.content);
            if (turn.activity) {
              return (
                <div key={turn.id} className="skill-runner-turn skill-runner-turn--assistant ask-turn-activity" dir="auto">
                  {activityBlock(turn.activity)}
                </div>
              );
            }
            if (turn.role === 'user') {
              return (
                <div key={turn.id} className="skill-runner-turn skill-runner-turn--user ask-bubble ask-bubble-user" dir="auto">
                  <div className="skill-runner-turn-content ask-bubble-content" dir={dir}>
                    {turn.content}
                    {turn.attachments && turn.attachments.length > 0 && (
                      <ul className="ask-bubble-attachments">
                        {turn.attachments.map((name, index) => (
                          <li key={`${name}-${index}`} dir="auto">
                            📎 {name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div
                key={turn.id}
                className={`skill-runner-turn skill-runner-turn--assistant ask-bubble ask-bubble-assistant${turn.notice ? ` ask-bubble-notice ask-bubble-notice--${turn.notice}` : ''}`}
                dir="auto"
              >
                <div className="skill-runner-turn-content ask-answer">
                  <AskMarkdown
                    content={turn.content}
                    dir={dir}
                    sources={turn.sources}
                    openSource={turn.sources ? sourceOpener(turn.id) : undefined}
                  />
                  {sourceMissing === turn.id && (
                    <p className="ask-source-missing" dir={chromeDir} role="status">
                      {t('ask.sourceNotFound')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {liveActivity && (
            <div className="skill-runner-turn skill-runner-turn--assistant ask-turn-activity ask-turn-live" dir="auto" aria-live="polite">
              {activityBlock(liveActivity)}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="skill-runner-composer ask-input-area" dir={chromeDir}>
          <ChatComposer
            value={draft}
            onChange={setDraft}
            onSubmit={async ({ text, attachments: att }) => {
              const trimmed = text.trim();
              if ((!trimmed && att.length === 0) || busy) return;
              const ok = await handleSend(trimmed, att);
              if (ok) setDraft('');
            }}
            disabled={inputDisabled}
            submitting={starting || stopping}
            placeholder={
              docLoading
                ? t('ask.loadingDocument')
                : stopping
                  ? t('ask.stopping')
                  : busy
                    ? t('ask.placeholderRunning')
                    : t('ask.placeholder')
            }
            layout="sticky"
            followUp={turns.length > 0}
            submitTitle={t('ask.sendQuestion')}
            showAgentModeSelector={false}
            // While an answer is being worked on, the send button is a stop button.
            onStop={following && !stopping ? handleStop : undefined}
            stopTitle={t('ask.stop')}
          />
        </div>

        {panelOpen && (
          <SavedConversations
            labels={{
              title: t('ask.saved.title'),
              close: t('ask.saved.close'),
              newConversation: t('ask.saved.new'),
              loading: t('ask.saved.loading'),
              empty: t('ask.saved.empty'),
              current: t('ask.saved.current'),
              confirmDelete: t('ask.saved.confirmDelete'),
              confirmDeleteYes: t('ask.saved.confirmDeleteYes'),
              confirmDeleteNo: t('ask.saved.confirmDeleteNo'),
              open: (title) => t('ask.saved.open', { title }),
              remove: (title) => t('ask.saved.remove', { title }),
              openFailed: t('ask.saved.openFailed'),
            }}
            rows={
              sessions &&
              sessions.map((item) => ({
                id: item.id,
                title: item.title || t('ask.saved.untitled'),
                running: item.running,
                detail: item.running
                  ? t('ask.saved.answering')
                  : [item.documentName, whenText(item.updatedAt)].filter(Boolean).join(' · '),
              }))
            }
            currentId={conversationId}
            failedId={failedId}
            onOpen={handleOpen}
            onDelete={handleDelete}
            onNew={startNew}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>
    </div>
  );
};
